import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Select, FormControl, InputLabel,
  Chip, IconButton, Card, CardContent, Avatar, Tooltip,
  LinearProgress, Menu, Divider, Alert
} from '@mui/material';
import {
  Add, MoreVert, AttachMoney, Person, CalendarToday,
  CheckCircle, Cancel, Edit, Delete, TrendingUp
} from '@mui/icons-material';
import {
  DndContext, DragOverlay, closestCenter, PointerSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '';
const token = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${token()}` });

// ─── Tarjeta de Oportunidad (draggable) ──────────────────────────────────────
function OportunidadCard({ op, onClick, onMenu }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: op.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  const dias = op.fecha_cierre_esperada
    ? Math.ceil((new Date(op.fecha_cierre_esperada) - new Date()) / 86400000)
    : null;
  const vencido = dias !== null && dias < 0;

  return (
    <Card ref={setNodeRef} style={style} {...attributes} {...listeners}
      sx={{ mb: 1, cursor: 'grab', '&:active': { cursor: 'grabbing' }, border: vencido ? '1px solid #f44336' : '1px solid #e0e0e0', borderRadius: 2 }}
      onClick={() => onClick(op)}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Typography variant="body2" fontWeight={600} sx={{ flex: 1, mr: 1 }}>{op.titulo}</Typography>
          <IconButton size="small" onClick={e => { e.stopPropagation(); onMenu(e, op); }}>
            <MoreVert fontSize="small" />
          </IconButton>
        </Box>
        {op.cliente_nombre && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Person sx={{ fontSize: 13, color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary">{op.cliente_nombre}</Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
          <Chip label={`$${parseFloat(op.valor_estimado || 0).toFixed(0)}`} size="small"
            icon={<AttachMoney sx={{ fontSize: '13px !important' }} />}
            sx={{ height: 20, fontSize: 11, bgcolor: '#e8f5e9', color: '#2e7d32' }} />
          <Chip label={`${op.probabilidad || 0}%`} size="small"
            sx={{ height: 20, fontSize: 11, bgcolor: '#e3f2fd', color: '#1565c0' }} />
        </Box>
        {dias !== null && (
          <Typography variant="caption" sx={{ color: vencido ? '#f44336' : 'text.secondary', mt: 0.5, display: 'block' }}>
            <CalendarToday sx={{ fontSize: 11, mr: 0.3 }} />
            {vencido ? `Venció hace ${Math.abs(dias)}d` : `${dias}d para cierre`}
          </Typography>
        )}
        <LinearProgress variant="determinate" value={op.probabilidad || 0}
          sx={{ mt: 1, height: 3, borderRadius: 2, bgcolor: '#eee',
            '& .MuiLinearProgress-bar': { bgcolor: op.probabilidad >= 70 ? '#4caf50' : op.probabilidad >= 40 ? '#ff9800' : '#f44336' } }} />
      </CardContent>
    </Card>
  );
}

// ─── Columna del Kanban ───────────────────────────────────────────────────────
function KanbanColumn({ etapa, oportunidades, onCardClick, onCardMenu, onAddClick }) {
  const count = oportunidades.length;
  const total = oportunidades.reduce((s, o) => s + parseFloat(o.valor_estimado || 0), 0);
  const isGanada = etapa.tipo === 'GANADA';
  const isPerdida = etapa.tipo === 'PERDIDA';

  return (
    <Box sx={{ minWidth: 260, maxWidth: 280, flex: '0 0 260px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, p: 1,
        bgcolor: etapa.color + '22', borderRadius: 2, borderLeft: `4px solid ${etapa.color}` }}>
        <Box>
          <Typography variant="body2" fontWeight={700} sx={{ color: etapa.color }}>{etapa.nombre}</Typography>
          <Typography variant="caption" color="text.secondary">{count} · ${total.toFixed(0)}</Typography>
        </Box>
        {!isGanada && !isPerdida && (
          <IconButton size="small" onClick={() => onAddClick(etapa)} sx={{ color: etapa.color }}>
            <Add fontSize="small" />
          </IconButton>
        )}
      </Box>
      <Box sx={{ minHeight: 200, bgcolor: '#f9fafb', borderRadius: 2, p: 1 }}>
        <SortableContext items={oportunidades.map(o => o.id)} strategy={verticalListSortingStrategy}>
          {oportunidades.map(op => (
            <OportunidadCard key={op.id} op={op} onClick={onCardClick} onMenu={onCardMenu} />
          ))}
        </SortableContext>
        {count === 0 && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 4 }}>
            Sin oportunidades
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Página principal Pipeline ─────────────────────────────────────────────────
export default function Pipeline() {
  const [pipeline, setPipeline] = useState(null);
  const [oportunidades, setOportunidades] = useState([]);
  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Dialogs
  const [dlgNueva, setDlgNueva] = useState(false);
  const [dlgDetalle, setDlgDetalle] = useState(null);
  const [dlgCerrar, setDlgCerrar] = useState(null);
  const [etapaInicial, setEtapaInicial] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [menuOp, setMenuOp] = useState(null);
  const [activeId, setActiveId] = useState(null);

  const [form, setForm] = useState({ titulo: '', valor_estimado: '', probabilidad: 20, fecha_cierre_esperada: '', notas: '' });
  const [formCerrar, setFormCerrar] = useState({ estado: 'GANADA', motivo_cierre: '' });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      setLoading(true);
      const [pRes, oRes, fRes] = await Promise.all([
        axios.get(`${API}/api/crm/pipelines`, { headers: headers() }),
        axios.get(`${API}/api/crm/oportunidades`, { headers: headers() }),
        axios.get(`${API}/api/crm/oportunidades/forecast`, { headers: headers() }),
      ]);
      setPipeline(pRes.data.data[0]);
      setOportunidades(oRes.data.data || []);
      setForecast(fRes.data.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Error cargando pipeline');
    } finally { setLoading(false); }
  }

  function opsByEtapa(etapaId) {
    return oportunidades.filter(o => o.etapa_id === etapaId && o.estado === 'ABIERTA');
  }

  async function handleDragEnd(event) {
    const { active, over } = event;
    setActiveId(null);
    if (!over || active.id === over.id) return;

    const op = oportunidades.find(o => o.id === active.id);
    if (!op) return;

    // Encontrar la etapa destino (puede ser el id de otra tarjeta o de una columna)
    let etapaDestino = null;
    for (const etapa of (pipeline?.etapas || [])) {
      const enEtapa = opsByEtapa(etapa.id);
      if (enEtapa.find(o => o.id === over.id)) { etapaDestino = etapa.id; break; }
    }
    // Si over.id es directamente una etapa
    if (!etapaDestino && pipeline?.etapas?.find(e => e.id === over.id)) etapaDestino = over.id;
    if (!etapaDestino || etapaDestino === op.etapa_id) return;

    const etapa = pipeline.etapas.find(e => e.id === etapaDestino);
    if (etapa?.tipo === 'GANADA' || etapa?.tipo === 'PERDIDA') {
      setDlgCerrar({ op, etapa_id: etapaDestino, estado: etapa.tipo });
      setFormCerrar({ estado: etapa.tipo, motivo_cierre: '' });
      return;
    }

    setOportunidades(prev => prev.map(o => o.id === active.id ? { ...o, etapa_id: etapaDestino } : o));
    try {
      await axios.put(`${API}/api/crm/oportunidades/${active.id}/etapa`, { etapa_id: etapaDestino }, { headers: headers() });
    } catch { loadData(); }
  }

  async function handleGuardar() {
    if (!form.titulo) return;
    try {
      const etapa_id = etapaInicial?.id || pipeline?.etapas?.[0]?.id;
      const pipeline_id = pipeline?.id;
      await axios.post(`${API}/api/crm/oportunidades`, { ...form, etapa_id, pipeline_id }, { headers: headers() });
      setDlgNueva(false);
      setForm({ titulo: '', valor_estimado: '', probabilidad: 20, fecha_cierre_esperada: '', notas: '' });
      loadData();
    } catch (e) { setError(e.response?.data?.error || 'Error guardando'); }
  }

  async function handleCerrar() {
    try {
      await axios.put(`${API}/api/crm/oportunidades/${dlgCerrar.op.id}/cerrar`,
        { ...formCerrar, etapa_id: dlgCerrar.etapa_id }, { headers: headers() });
      setDlgCerrar(null);
      loadData();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  async function handleEliminar(op) {
    if (!window.confirm(`¿Eliminar "${op.titulo}"?`)) return;
    try {
      await axios.delete(`${API}/api/crm/oportunidades/${op.id}`, { headers: headers() });
      setMenuAnchor(null);
      loadData();
    } catch (e) { setError(e.response?.data?.error || 'Error'); }
  }

  if (loading) return <LinearProgress />;

  const etapas = pipeline?.etapas || [];
  const etapasAbiertas = etapas.filter(e => !e.tipo);
  const etapasCerradas = etapas.filter(e => e.tipo);

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Pipeline de Ventas</Typography>
          <Typography variant="body2" color="text.secondary">{pipeline?.nombre}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => { setEtapaInicial(null); setDlgNueva(true); }}>
          Nueva Oportunidad
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Métricas */}
      {forecast && (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[
            { label: 'Pipeline Total', value: `$${forecast.total_pipeline?.toFixed(2) || '0.00'}`, color: '#1565c0', bg: '#e3f2fd' },
            { label: 'Forecast Mes', value: `$${forecast.forecast?.toFixed(2) || '0.00'}`, color: '#2e7d32', bg: '#e8f5e9' },
            { label: 'Oportunidades Abiertas', value: forecast.cantidad_abiertas || 0, color: '#e65100', bg: '#fff3e0' },
          ].map(m => (
            <Box key={m.label} sx={{ p: 2, borderRadius: 2, bgcolor: m.bg, flex: '1 1 160px', minWidth: 140 }}>
              <Typography variant="h6" fontWeight={700} sx={{ color: m.color }}>{m.value}</Typography>
              <Typography variant="caption" color="text.secondary">{m.label}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {/* Kanban */}
      <DndContext sensors={sensors} collisionDetection={closestCenter}
        onDragStart={e => setActiveId(e.active.id)} onDragEnd={handleDragEnd}>
        <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 2, alignItems: 'flex-start' }}>
          {etapasAbiertas.map(etapa => (
            <KanbanColumn key={etapa.id} etapa={etapa} oportunidades={opsByEtapa(etapa.id)}
              onCardClick={op => setDlgDetalle(op)}
              onCardMenu={(e, op) => { setMenuAnchor(e.currentTarget); setMenuOp(op); }}
              onAddClick={e => { setEtapaInicial(e); setDlgNueva(true); }} />
          ))}
          <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
          {etapasCerradas.map(etapa => (
            <KanbanColumn key={etapa.id} etapa={etapa} oportunidades={oportunidades.filter(o => o.etapa_id === etapa.id)}
              onCardClick={op => setDlgDetalle(op)}
              onCardMenu={(e, op) => { setMenuAnchor(e.currentTarget); setMenuOp(op); }}
              onAddClick={() => {}} />
          ))}
        </Box>
        <DragOverlay>
          {activeId ? (() => { const op = oportunidades.find(o => o.id === activeId); return op ? <Card sx={{ p: 1.5, width: 250, boxShadow: 8 }}><Typography variant="body2" fontWeight={600}>{op.titulo}</Typography></Card> : null; })() : null}
        </DragOverlay>
      </DndContext>

      {/* Menu oportunidad */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem onClick={() => { setDlgDetalle(menuOp); setMenuAnchor(null); }}><Edit sx={{ mr: 1, fontSize: 18 }} />Editar</MenuItem>
        <MenuItem onClick={() => { setDlgCerrar({ op: menuOp, estado: 'GANADA' }); setFormCerrar({ estado: 'GANADA', motivo_cierre: '' }); setMenuAnchor(null); }}>
          <CheckCircle sx={{ mr: 1, fontSize: 18, color: '#4caf50' }} />Marcar Ganada
        </MenuItem>
        <MenuItem onClick={() => { setDlgCerrar({ op: menuOp, estado: 'PERDIDA' }); setFormCerrar({ estado: 'PERDIDA', motivo_cierre: '' }); setMenuAnchor(null); }}>
          <Cancel sx={{ mr: 1, fontSize: 18, color: '#9e9e9e' }} />Marcar Perdida
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => { handleEliminar(menuOp); }} sx={{ color: 'error.main' }}><Delete sx={{ mr: 1, fontSize: 18 }} />Eliminar</MenuItem>
      </Menu>

      {/* Dialog nueva oportunidad */}
      <Dialog open={dlgNueva} onClose={() => setDlgNueva(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nueva Oportunidad {etapaInicial ? `— ${etapaInicial.nombre}` : ''}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField label="Título *" value={form.titulo} onChange={e => setForm(p => ({ ...p, titulo: e.target.value }))} fullWidth />
          <TextField label="Valor estimado ($)" type="number" value={form.valor_estimado} onChange={e => setForm(p => ({ ...p, valor_estimado: e.target.value }))} fullWidth />
          <TextField label="Probabilidad (%)" type="number" inputProps={{ min: 0, max: 100 }} value={form.probabilidad} onChange={e => setForm(p => ({ ...p, probabilidad: e.target.value }))} fullWidth />
          <TextField label="Fecha cierre esperada" type="date" value={form.fecha_cierre_esperada} onChange={e => setForm(p => ({ ...p, fecha_cierre_esperada: e.target.value }))} InputLabelProps={{ shrink: true }} fullWidth />
          <TextField label="Notas" multiline rows={3} value={form.notas} onChange={e => setForm(p => ({ ...p, notas: e.target.value }))} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgNueva(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleGuardar} disabled={!form.titulo}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog cerrar oportunidad */}
      <Dialog open={Boolean(dlgCerrar)} onClose={() => setDlgCerrar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Cerrar Oportunidad</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>Estado</InputLabel>
            <Select value={formCerrar.estado} label="Estado" onChange={e => setFormCerrar(p => ({ ...p, estado: e.target.value }))}>
              <MenuItem value="GANADA"><CheckCircle sx={{ color: '#4caf50', mr: 1 }} />Ganada</MenuItem>
              <MenuItem value="PERDIDA"><Cancel sx={{ color: '#9e9e9e', mr: 1 }} />Perdida</MenuItem>
            </Select>
          </FormControl>
          <TextField label="Motivo" multiline rows={2} value={formCerrar.motivo_cierre} onChange={e => setFormCerrar(p => ({ ...p, motivo_cierre: e.target.value }))} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgCerrar(null)}>Cancelar</Button>
          <Button variant="contained" color={formCerrar.estado === 'GANADA' ? 'success' : 'inherit'} onClick={handleCerrar}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog detalle */}
      {dlgDetalle && (
        <Dialog open onClose={() => setDlgDetalle(null)} maxWidth="sm" fullWidth>
          <DialogTitle>{dlgDetalle.titulo}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Valor: <strong>${parseFloat(dlgDetalle.valor_estimado || 0).toFixed(2)}</strong> · Probabilidad: <strong>{dlgDetalle.probabilidad}%</strong>
            </Typography>
            {dlgDetalle.notas && <Typography variant="body2" sx={{ mt: 1 }}>{dlgDetalle.notas}</Typography>}
            <Chip label={dlgDetalle.estado} size="small" sx={{ mt: 1 }}
              color={dlgDetalle.estado === 'GANADA' ? 'success' : dlgDetalle.estado === 'PERDIDA' ? 'default' : 'primary'} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDlgDetalle(null)}>Cerrar</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
}
